import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { GIFObject, GifSearchResponse } from './tenor.types'

interface UseGifsResult {
  gifs: GIFObject[]
  error: Error | undefined
  isLoadingMore: boolean
  isReachingEnd: boolean
  loadMore: () => void
}

export function useGifs(query: string, limit = 16): UseGifsResult {
  const [gifs, setGifs] = useState<GIFObject[]>([])
  const [position, setPosition] = useState<string | null>(null)
  const [nextPosition, setNextPosition] = useState<string | null>(null)
  const [isReachingEnd, setIsReachingEnd] = useState(false)

  useEffect(() => {
    setGifs([])
    setPosition(null)
    setNextPosition(null)
    setIsReachingEnd(false)
  }, [query])

  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, GifSearchResponse>({
    method: RequestMethodsEnum.get,
  })

  const trimmedQuery = query.trim()
  const queryParams = new URLSearchParams()

  if (position !== null && position !== '') {
    queryParams.set('position', position)
  }
  if (limit) {
    queryParams.set('limit', limit.toString())
  }
  if (trimmedQuery) {
    queryParams.set('query', trimmedQuery)
  }

  const { data, error } = useSWR<GifSearchResponse>(
    `/api/community/gifs?${queryParams.toString()}`,
    fetcher,
  )

  useEffect(() => {
    if (data) {
      setGifs(prevGifs => [...prevGifs, ...data.results])
      setNextPosition(data.next || null)
      if (!data.next) {
        setIsReachingEnd(true)
      }
    }
  }, [data])

  const isLoadingMore = !data && !error

  const loadMore = (): void => {
    if (!isReachingEnd && !isLoadingMore) {
      setPosition(nextPosition)
    }
  }

  return {
    gifs,
    error,
    isLoadingMore,
    isReachingEnd,
    loadMore,
  }
}
